import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Permissions } from "@evergis/api";

import { getUsername } from "../../../ducks/user";
import { getIsTempProject } from "../utils/getIsTempProject";
import { useProjectInfo } from "../../../ducks/project/hooks";
import { usePrintModule } from "../../../hooks/appConfig/useAppModule";
import { useProjectName } from "../../../hooks/project/useProjectName";
import { useProjectLayers } from "../../../hooks/project/useProjectLayers";
import { useIsShared } from "../../../hooks/api/useIsShared";
import { useUser } from "../../../hooks/user/useUser";

export interface PanelConfig {
  configurable: boolean;
  printEnabled: boolean;
  isOwner: boolean;
  hasLayers: boolean;
  isTempProject: boolean;
  shared: boolean;
  userRoles: string[];
}

export const usePanelConfig = (): PanelConfig => {
  const user = useUser();
  const shared = useIsShared();
  const projectName = useProjectName();
  const isTempProject = getIsTempProject(projectName);
  const printModule = usePrintModule();
  const username = useSelector(getUsername);
  const [{ permissions, name, owner }] = useProjectInfo();
  const layers = useProjectLayers();

  return useMemo(
    () => ({
      configurable: permissions && permissions.includes(Permissions.Configure),
      printEnabled: !!printModule,
      isOwner: owner && owner === username && !!name,
      hasLayers: !!layers.length,
      userRoles: user?.roles,
      isTempProject,
      shared,
    }),
    [permissions, printModule, owner, username, name, layers.length, isTempProject, shared, user?.roles],
  );
};
